import { isScopeBoundary, scopedDescendants } from '../../core/canopy/stateScope';

// ---------------------------------------------------------------------------
// State group BOUNDARIES — independent nested state groups.
//
// A state "group" is an owner object plus the members in its subtree that
// coordinate by state name. A group BOUNDARY is an object that explicitly OWNS
// its state group — marked in authored data by `PropertiesData.stateGroup` (see
// propertiesdata.ts). An outer group's coordination stops at a boundary: the
// boundary object and everything below it belong to the INNER group and are
// pruned from the outer group's walks.
//
// The boundary predicate and the pruned-subtree walk now live in the RUNTIME
// module `core/canopy/stateScope.ts` — `trace/setState` is a scoped runtime op
// (STATE_SCOPE_SPEC.md), so the fan-out membership and the editor's coordination
// walks MUST agree. This file re-exports them under their editor names so the
// existing statesActions / stateContext / thumbnail call sites are unchanged.
// See docs-internal/STATE_GROUP_BOUNDARIES.md.
// ---------------------------------------------------------------------------

/**
 * True when `obj` owns its state group — it is a group boundary. Ownership is
 * DATA: `obj` carries the `stateGroup` marker on its Properties. Two paths write
 * it:
 *
 *  1. The "Separate states" gesture — marks any object as an independent owner.
 *  2. Rig creation — `wrapInRigContainer` bakes a marked Properties into every
 *     rig, so a rig is a boundary from the instant it exists (see rigContainer.ts
 *     / STATE_RIG_CONTAINERS.md). No rig special-case lives here: a rig reads as a
 *     boundary because it carries the marker, like any other owner.
 *
 * A PLAIN self-owned owner (first "+ State" on a bare object, no rig, no explicit
 * gesture) is deliberately NOT marked: it JOINS an ancestor group by default (the
 * "nested add joins, not nests" rule). Nesting is opt-in.
 *
 * Alias of the runtime `isScopeBoundary` (core/canopy/stateScope.ts).
 */
export const isGroupBoundary = isScopeBoundary;

/**
 * The owner's group subtree: every descendant of `owner` EXCEPT those in a
 * subtree rooted at a boundary below it. A boundary node and everything under
 * it belong to the inner group and are excluded — they are coordinated by that
 * inner owner, not by `owner`. `owner` itself is never included and never
 * treated as its own boundary.
 *
 * Alias of the runtime `scopedDescendants` (core/canopy/stateScope.ts), so the
 * editor's fan-out/switch/ref-heal walks share the runtime's exact scope
 * membership. Result is pre-order DFS.
 */
export const groupDescendants = scopedDescendants;
